import React from 'react'
import './YumCountrySelect.scss'
import { currentLangString } from '../../i18nInit'

import countries from 'i18n-iso-countries'
import supportedLocales from 'i18n-iso-countries/supportedLocales.json'
import YumSelect, { YumSelectProps } from '../YumSelect/YumSelect'
import Autocomplete from '@material-ui/lab/Autocomplete'
import YumField from '../YumInput/YumInput'
import { SelectInputProps } from '@material-ui/core/Select/SelectInput'

import { ReactComponent as CountryActiveIcon } from '../../assets/v3/icons/country-active.svg'
import { ReactComponent as CountryInactiveIcon } from '../../assets/v3/icons/country-inactive.svg'
import { t } from 'ttag'

countries.registerLocale(require('i18n-iso-countries/langs/en.json'))

if (supportedLocales.includes(currentLangString)) {
  // TODO: Only load the needed ones (with webpack)?
  countries.registerLocale(require(`i18n-iso-countries/langs/${currentLangString}.json`))
}

function supportsFlagEmojiCheck() {
  const canvas = document.createElement('canvas')
  canvas.height = 10
  canvas.width = canvas.height * 2
  const ctx = canvas.getContext('2d')
  if (ctx) {
    ctx.font = canvas.height + 'px Arial'
    ctx.fillText('🇬🇧', 0, canvas.height)
    const data = ctx.getImageData(0, 0, canvas.width, canvas.height).data
    let i = 0
    while (i < data.length) {
      if (data[i] !== data[i + 1] || data[i] !== data[i + 2]) return true
      i += 4
    }
  }
  return false
}
const supportsFlagEmoji = supportsFlagEmojiCheck()

// ISO 3166-1 alpha-2
// ⚠️ No support for IE 11
export function countryToFlag(isoCode: string, emojiOnly = false) {
  if (!supportsFlagEmoji && emojiOnly) return ''
  if (isoCode === 'MEA') return '🏴'
  if (!supportsFlagEmoji) {
    return <img className={'flag'} width={16} src={`https://flagcdn.com/${isoCode.toLowerCase()}.svg`} alt="flag" />
  }
  return typeof String.fromCodePoint !== 'undefined'
    ? isoCode.toUpperCase().replace(/./g, (char) => String.fromCodePoint(char.charCodeAt(0) + 127397))
    : isoCode
}

export function countryWithFlag(countryCode: string) {
  let country = countries.getName(countryCode, currentLangString)
  if (!country) country = countries.getName(countryCode, 'en')
  if (!country) country = countryCode

  return country ? (
    <>
      <span style={{ marginRight: 2, lineHeight: 1 }}>{countryToFlag(countryCode)}</span>
      {country}
    </>
  ) : (
    ''
  )
}

interface YumCountrySelectProps extends YumSelectProps {
  countries: string[]
  required?: boolean

  onChange?: SelectInputProps['onChange']
  value?: unknown
  error?: any
  label?: any
  style?: any
  ref?: any

  startIcon?: any
  startInactiveIcon?: any

  showIcon?: boolean
  useAutocompleteSelect?: boolean
}

export interface LocalizedCountry {
  code: string
  country: string
  countryWithFlag: string
  //countryWithFlagJsx: JSX.Element
}

export function localizeCountries(enabledCountries: string[], emojiOnly = false): LocalizedCountry[] {
  let countryOptions = Object.entries(countries.getNames(currentLangString))
  if (countryOptions.length === 0) countryOptions = Object.entries(countries.getNames('en'))
  if (enabledCountries && enabledCountries.length > 0) {
    countryOptions = countryOptions.filter((country: [string, string]) => enabledCountries.includes(country[0]))
  }
  return countryOptions.map((c: [string, string]) => {
    const code = c[0]
    const country = c[1]
    const flag = countryToFlag(code, emojiOnly)
    const countryWithFlag = flag + ' ' + country
    /*const countryWithFlagJsx = (
      <>
        <span style={{ marginRight: 2, lineHeight: 1 }}>{flag}</span>
        {country}
      </>
    )*/
    return { code, country, countryWithFlag /*, countryWithFlagJsx*/ }
  })
}

export default function YumCountrySelect({
  countries,
  required,
  value,
  showIcon,
  useAutocompleteSelect,
  ...props
}: YumCountrySelectProps) {
  const { ref, ...otherProps } = props
  const options = localizeCountries(countries, true)

  return useAutocompleteSelect ? (
    <Autocomplete
      clearText={t`Clear`}
      closeText={t`Close`}
      openText={t`Open`}
      noOptionsText={t`No options`}
      openOnFocus
      onChange={(event, newValue) => {
        // @ts-ignore
        props.onChange?.({ target: { value: newValue?.code } }, null)
      }}
      options={options}
      value={options.find((d) => d.code === value) || null}
      getOptionLabel={(option) => option.country}
      renderOption={(option) => option.countryWithFlag}
      getOptionSelected={(option, value) => option.code === value.code}
      renderInput={(params) => (
        <YumField
          {...params}
          label={props.label}
          style={props.style}
          startIcon={showIcon && CountryActiveIcon}
          startIconInactive={showIcon && CountryInactiveIcon}
          value={value}
          error={props.error}
          variant="filled"
          InputProps={{
            ...params.InputProps,
            disableUnderline: true,
            // This disables the browser autocomplete.
            autoComplete: 'new-password',
          }}
          fullWidth={true}
          type="text"
        />
      )}
    />
  ) : (
    <YumSelect
      {...otherProps}
      value={value}
      startIcon={showIcon && CountryActiveIcon}
      startInactiveIcon={showIcon && CountryInactiveIcon}
    >
      {required ? '' : <option value="" />}
      {options.map(({ code, countryWithFlag }: LocalizedCountry, index: number) => (
        <option key={index} value={code}>
          {countryWithFlag}
        </option>
      ))}
    </YumSelect>
  )
}
